/// <reference types="google.maps" />

'use client'

import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AdvancedMarker, InfoWindow, Map, useMap } from '@vis.gl/react-google-maps'
import { Box } from '@chakra-ui/react'
import { HTMLArticle } from 'ui'
import type { MapLocatorTag } from 'ecosystem'
import LocationMarker from './LocationMarker'
import { useClusteredMarkers } from './useClusteredMarkers'
import type {
  FocusRequest,
  LocatorOrigin,
  MapLocatorEntry,
  MapLocatorSettings
} from './useMapLocator'
import {
  DEFAULT_CLUSTER_MAX_ZOOM,
  DEFAULT_ORIGIN_MARKER_SETTINGS,
  DEMO_MAP_ID,
  MAP_LOCATOR_CN,
  mapLocatorCn as cn,
  VIEWPORT_CULLING_THRESHOLD
} from './constants'
import { markerSvg, PIN_MARKER_HEIGHT } from './markerSvg'
import { formatDistance, isWithinBounds, itemPosition, popoverContent } from './utils'

/* Module-level: vis.gl re-opens the info window whenever this prop changes
 * identity, and an inline literal would be a new array on every render. */
const INFO_WINDOW_OFFSET: [number, number] = [0, -PIN_MARKER_HEIGHT]

type MarkerInstance = google.maps.marker.AdvancedMarkerElement

interface MapCanvasProps {
  entries: MapLocatorEntry[]
  settings: MapLocatorSettings
  origin: LocatorOrigin | null
  activeItem: MapLocatorEntry | null
  focus: FocusRequest | null
  onSelect: (item: MapLocatorEntry | null) => void
}

/**
 * Keeps the camera in step with the filtered set without fighting the user:
 * it only re-frames when the result set or the origin actually changes.
 */
const CameraController = ({
  entries,
  origin,
  focus,
  fitBoundsToItems,
  declusteredZoom
}: {
  entries: MapLocatorEntry[]
  origin: LocatorOrigin | null
  focus: FocusRequest | null
  fitBoundsToItems?: boolean
  /** First zoom level at which the clusterer stops grouping points. */
  declusteredZoom: number
}) => {
  const map = useMap()
  const lastFrame = useRef<string>('')

  useEffect(() => {
    if (!map || !fitBoundsToItems || !entries.length) {
      return
    }

    const signature = `${origin ? `${origin.lat},${origin.lng}` : ''}|${entries
      .map((entry) => entry.id)
      .join(',')}`

    if (signature === lastFrame.current) {
      return
    }

    lastFrame.current = signature

    // With an origin the useful frame is "you and your nearest options",
    // not every point that survived the filter.
    const framed = origin ? entries.slice(0, 5) : entries

    if (framed.length === 1 && !origin) {
      map.setCenter(itemPosition(framed[0] as MapLocatorEntry))
      map.setZoom(14)

      return
    }

    // A plain LatLngBoundsLiteral rather than `new google.maps.LatLngBounds()`:
    // if a second Google Maps script is present on the page (the legacy
    // GOOGLE_MAP_WIDGET loads its own), a bounds object built from one instance
    // fails the `instanceof` check inside the other's fitBounds. Literals carry
    // no class identity, so they work whichever instance won the global.
    const points = framed
      .map(itemPosition)
      .concat(origin ? [{ lat: origin.lat, lng: origin.lng }] : [])

    map.fitBounds(
      {
        north: Math.max(...points.map((point) => point.lat)),
        south: Math.min(...points.map((point) => point.lat)),
        east: Math.max(...points.map((point) => point.lng)),
        west: Math.min(...points.map((point) => point.lng))
      },
      48
    )
  }, [map, entries, origin, fitBoundsToItems])

  useEffect(() => {
    if (!map || !focus) {
      return
    }

    const active = entries.find((entry) => entry.id === focus.id)

    if (!active) {
      return
    }

    const center = itemPosition(active)

    if (!focus.zoomIn) {
      map.panTo(center)

      return
    }

    // Below the clustering cut-off the point is drawn as a cluster badge, so
    // centring alone would leave the visitor looking at a number. One
    // moveCamera keeps it to a single camera change, and a single re-cluster.
    map.moveCamera({
      center,
      zoom: Math.max(map.getZoom() ?? 0, declusteredZoom)
    })
    // Keyed to the focus request, not to every change of the result set
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, focus?.nonce])

  return null
}

/** Above the culling threshold only points inside the current viewport are mounted. */
const useVisibleEntries = (entries: MapLocatorEntry[]) => {
  const map = useMap()
  const [bounds, setBounds] = useState<google.maps.LatLngBoundsLiteral | null>(null)
  const culling = entries.length > VIEWPORT_CULLING_THRESHOLD

  useEffect(() => {
    if (!map || !culling) {
      return
    }

    const readBounds = () => {
      const currentBounds = map.getBounds()

      if (currentBounds) {
        setBounds(currentBounds.toJSON())
      }
    }

    readBounds()

    const listener = map.addListener('idle', readBounds)

    return () => listener.remove()
  }, [map, culling])

  return useMemo(() => {
    if (!culling || !bounds) {
      return entries
    }

    return entries.filter((entry) => isWithinBounds(itemPosition(entry), bounds))
  }, [entries, bounds, culling])
}

const LocatorMarkers = ({
  entries,
  settings,
  activeItem,
  onSelect
}: {
  entries: MapLocatorEntry[]
  settings: MapLocatorSettings
  activeItem: MapLocatorEntry | null
  onSelect: (item: MapLocatorEntry | null) => void
}) => {
  const { setMarkerRef, clusteringEnabled } = useClusteredMarkers({
    ...settings.clustering,
    borderColor: settings.clustering.borderColor ?? settings.markers.borderColor,
    borderWidth: settings.clustering.borderWidth ?? settings.markers.borderWidth
  })
  const visibleEntries = useVisibleEntries(entries)

  const handleRegister = useCallback(
    (id: string, marker: MarkerInstance | null) => setMarkerRef(marker, id),
    [setMarkerRef]
  )

  const handleCloseClick = useCallback(() => onSelect(null), [onSelect])

  const infoWindowContent = useMemo(() => {
    if (!activeItem || settings.popover.mode !== 'INFO_WINDOW') {
      return null
    }

    return popoverContent(settings.popover, {
      item: activeItem,
      tags: settings.tags as MapLocatorTag[],
      distanceLabel:
        settings.search.showDistance && activeItem.distanceKm !== undefined
          ? formatDistance(activeItem.distanceKm, settings.labels.distanceUnit)
          : undefined
    })
  }, [activeItem, settings])

  return (
    <>
      {/*
       * Keyed on the clustering mode so the markers remount when it is toggled.
       * Tearing the clusterer down detaches every marker it owned, and vis.gl's
       * own "attach to map" effect has already run — without a remount the map
       * would simply go empty when clustering is switched off.
       */}
      <Fragment key={clusteringEnabled ? 'clustered' : 'unclustered'}>
        {visibleEntries.map((entry) => (
          <LocationMarker
            isActive={activeItem?.id === entry.id}
            item={entry}
            key={entry.id}
            markers={settings.markers}
            onRegister={handleRegister}
            onSelect={onSelect}
            tags={settings.tags}
          />
        ))}
      </Fragment>

      {activeItem && infoWindowContent ? (
        /*
         * Anchored to a position rather than to the marker element. The clusterer
         * re-runs on every map `idle` and detaches any marker that has just been
         * absorbed into a cluster — with a marker anchor the info window closed
         * the instant the map finished moving. A position outlives that churn.
         */
        <InfoWindow
          headerDisabled
          /*
           * `onCloseClick`, not `onClose`: the maps `close` event also fires for
           * the programmatic `infoWindow.close()` that vis.gl runs whenever its
           * open-effect re-runs. Clearing the selection from there cancelled the
           * selection that had just been made — every second pick, from the list
           * and from a marker alike, opened the window and closed it again.
           */
          onCloseClick={handleCloseClick}
          pixelOffset={INFO_WINDOW_OFFSET}
          position={itemPosition(activeItem)}
        >
          <Box className={cn(MAP_LOCATOR_CN.POPOVER)} maxW="280px">
            <HTMLArticle content={infoWindowContent} />
          </Box>
        </InfoWindow>
      ) : null}
    </>
  )
}

const MapCanvas = ({ entries, settings, origin, activeItem, focus, onSelect }: MapCanvasProps) => {
  const { map: mapSettings } = settings
  const { controls = {} } = mapSettings

  const handleMapClick = useCallback(() => onSelect(null), [onSelect])

  return (
    <Map
      className={cn(MAP_LOCATOR_CN.MAP)}
      clickableIcons={false}
      defaultCenter={mapSettings.defaultCenter}
      defaultZoom={mapSettings.defaultZoom}
      disableDefaultUI
      fullscreenControl={controls.fullscreen ?? true}
      gestureHandling={mapSettings.gestureHandling ?? 'cooperative'}
      mapId={mapSettings.mapId || DEMO_MAP_ID}
      mapTypeControl={controls.mapType ?? false}
      maxZoom={mapSettings.maxZoom}
      minZoom={mapSettings.minZoom}
      onClick={handleMapClick}
      reuseMaps
      streetViewControl={controls.streetView ?? false}
      zoomControl={controls.zoom ?? true}
    >
      <LocatorMarkers
        activeItem={activeItem}
        entries={entries}
        onSelect={onSelect}
        settings={settings}
      />

      {origin ? (
        <AdvancedMarker position={origin} title={origin.label} zIndex={3}>
          <div
            className={cn(MAP_LOCATOR_CN.ORIGIN)}
            dangerouslySetInnerHTML={{
              __html: markerSvg({
                fill: settings.originMarker.color || DEFAULT_ORIGIN_MARKER_SETTINGS.color || '',
                borderColor: settings.originMarker.borderColor,
                borderWidth: settings.originMarker.borderWidth,
                glyph: { type: 'none' },
                shape: 'CIRCLE',
                size: settings.originMarker.size
              })
            }}
            style={{
              lineHeight: 0,
              // AdvancedMarker anchors content by its bottom edge; a dot wants its
              // centre on the coordinate instead
              transform: 'translateY(50%)'
            }}
          />
        </AdvancedMarker>
      ) : null}

      <CameraController
        declusteredZoom={(settings.clustering.maxZoom ?? DEFAULT_CLUSTER_MAX_ZOOM) + 1}
        entries={entries}
        fitBoundsToItems={mapSettings.fitBoundsToItems}
        focus={focus}
        origin={origin}
      />
    </Map>
  )
}

/* Memoised so a keystroke, which only changes state upstream, does not walk
 * this subtree before React has painted the character. */
export default memo(MapCanvas)
